import * as React from 'react';
import { observer } from 'mobx-react';
import { Link, useLocation } from 'react-router-dom';
import { BreadcrumbItem, Breadcrumbs, BurgerMenu, MenuRow } from '@ixicare/ui';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useSidebarCollapse } from '~hooks/useSidebarCollapse';
import TopBar from '~components/TopBar/TopBar';
import SiteSelector from '~components/TopBar/SiteSelector';
import DashboardView from '~components/DashboardView';
import UserNotifications from '~containers/UI/UserNotifications';
import BackLink from '~components/BackLink';
import { PageTitle, PageTitleProvider } from './PageTitle';
import { BreadcrumbsProvider } from './Breadcrumbs';
import styles from './DashboardWrapper.module.css';

interface DashboardWrapperProps {
  children: React.ReactNode;
  routes: MenuRow[];
}

const DashboardWrapper: React.FC<DashboardWrapperProps> = observer(({ children, routes }) => {
  const { auth, sites } = useStores();
  const location = useLocation();
  const { collapsed, toggle, offCanvas, drawerOpen, openDrawer, closeDrawer } = useSidebarCollapse();
  const [{ title, heading }, setPageTitle] = React.useState<PageTitle>({});
  const [crumbs, setCrumbs] = React.useState<{ prefix: BreadcrumbItem[]; page: BreadcrumbItem[] }>({
    prefix: [],
    page: [],
  });
  const breadcrumbsContext = React.useMemo(
    () => ({
      setPrefix: (prefix: BreadcrumbItem[]) => setCrumbs((current) => ({ ...current, prefix })),
      setPage: (page: BreadcrumbItem[]) => setCrumbs((current) => ({ ...current, page })),
    }),
    []
  );

  React.useEffect(() => {
    if (title) {
      document.title = t('meta.title', { title });
    }
  }, [title]);

  const trail = [...crumbs.prefix, ...crumbs.page];
  // The ui trail never links its last crumb. One that names another entity (the resident an alert is
  // about) still should, so it is linked here unless it points back at the current page.
  const last = trail[trail.length - 1];
  const items: BreadcrumbItem[] =
    last?.href && last.href.split(/[?#]/)[0] !== location.pathname
      ? [
          ...trail.slice(0, -1),
          {
            label: (
              <Link to={last.href} className={styles.CrumbLink}>
                {last.label}
              </Link>
            ),
          },
        ]
      : trail;
  // The back target is the page's parent, which the trail already names: the last linkable crumb
  // before the current page (the trailing crumb is the page itself and never links).
  const parent = items
    .slice(0, -1)
    .reverse()
    .find((i) => i.href);

  const siteName = sites.selectedSite?.name;
  const menuRows = siteName
    ? routes.map((row) => ('siteHeader' in row ? { ...row, siteName } : row))
    : routes.filter((row) => !('siteHeader' in row));

  return (
    <div id="page-wrap-outer">
      <div className="no-print">
        {/* The nav is off-canvas below 550px, so the bar carries the only way
            to reach it. Above that it opens nothing and shows no burger. */}
        <TopBar onOpenNav={offCanvas ? openDrawer : undefined} navOpen={drawerOpen} />
      </div>
      {auth.user && (
        <div className="no-print">
          {/* The nav's site header is the same switcher as the top bar's — it
              marks where the site-scoped items start, so switching from there
              is what you'd reach for while reading them. */}
          <BurgerMenu
            routes={menuRows}
            siteHeaderSlot={<SiteSelector variant="sidebar" />}
            collapsed={collapsed}
            onToggleCollapse={toggle}
            collapseLabel={t('menu.dashboard.collapseSidebar')}
            expandLabel={t('menu.dashboard.expandSidebar')}
            offCanvas={offCanvas}
            open={drawerOpen}
            onRequestClose={closeDrawer}
            closeLabel={t('global.closeMenu')}
          />
        </div>
      )}
      <main id="page-wrap-main">
        <PageTitleProvider value={setPageTitle}>
          <BreadcrumbsProvider value={breadcrumbsContext}>
            <DashboardView
              title={heading}
              breadcrumbs={
                items.length > 0 && (
                  <>
                    <Breadcrumbs items={items} />
                    {parent?.href && (
                      <BackLink
                        fallbackUrl={parent.href}
                        name={t('site.crumbs.backTo', { name: String(parent.label) })}
                      />
                    )}
                  </>
                )
              }
            >
              <UserNotifications>{children}</UserNotifications>
            </DashboardView>
          </BreadcrumbsProvider>
        </PageTitleProvider>
      </main>
    </div>
  );
});

export default DashboardWrapper;
